/**
 * 格式化时间（秒数 -> 时分秒）
 * @param {number} seconds - 秒数
 * @param {boolean} showHours - 是否显示小时部分（默认自动判断）
 * @returns {string} 格式化后的时间字符串
 */
export function formatTime (seconds, showHours = null) {
  // 处理负数或非数字
  if (isNaN(seconds) || seconds < 0) {
    return '00:00'
  }

  // 取整秒数
  const sec = Math.floor(seconds)

  // 计算时分秒
  const hours = Math.floor(sec / 3600)
  const minutes = Math.floor((sec % 3600) / 60)
  const remainingSeconds = sec % 60

  // 自动判断是否显示小时部分
  const shouldShowHours = showHours !== null ? showHours : hours > 0

  if (shouldShowHours) {
    // 格式: HH:MM:SS
    return [
      hours.toString().padStart(2, '0'),
      minutes.toString().padStart(2, '0'),
      remainingSeconds.toString().padStart(2, '0')
    ].join(':')
  } else {
    // 格式: MM:SS
    return [
      minutes.toString().padStart(2, '0'),
      remainingSeconds.toString().padStart(2, '0')
    ].join(':')
  }
}

/**
 * 格式化时间带毫秒
 * @param {number} milliseconds - 毫秒数
 * @returns {string} 格式化后的时间字符串
 */
export function formatTimeWithMs (milliseconds) {
  if (isNaN(milliseconds) || milliseconds < 0) {
    return '00:00.000'
  }

  const totalSeconds = milliseconds / 1000
  const hours = Math.floor(totalSeconds / 3600)
  const minutes = Math.floor((totalSeconds % 3600) / 60)
  const seconds = Math.floor(totalSeconds % 60)
  const ms = Math.floor((milliseconds % 1000))

  if (hours > 0) {
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}.${ms.toString().padStart(3, '0')}`
  } else {
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}.${ms.toString().padStart(3, '0')}`
  }
}

/**
 * 格式化为人类可读的时间描述
 * @param {number} seconds - 秒数
 * @returns {string} 人类可读的时间描述
 */
export function formatHumanReadable (seconds) {
  if (isNaN(seconds) || seconds < 0) {
    return '0秒'
  }

  const sec = Math.floor(seconds)

  const days = Math.floor(sec / 86400)
  const hours = Math.floor((sec % 86400) / 3600)
  const minutes = Math.floor((sec % 3600) / 60)
  const remainingSeconds = sec % 60

  const parts = []

  if (days > 0) parts.push(`${days}天`)
  if (hours > 0) parts.push(`${hours}小时`)
  if (minutes > 0) parts.push(`${minutes}分钟`)
  if (remainingSeconds > 0 || parts.length === 0) {
    parts.push(`${remainingSeconds}秒`)
  }

  return parts.join('')
}

/**
 * 将时间字符串解析为秒数
 * @param {string} timeString - 时间字符串 (HH:MM:SS 或 MM:SS)
 * @returns {number} 秒数
 */
export function parseTimeToSeconds (timeString) {
  if (!timeString) return 0

  const parts = timeString.split(':').map(Number)

  if (parts.length === 3) {
    // HH:MM:SS
    return parts[0] * 3600 + parts[1] * 60 + parts[2]
  } else if (parts.length === 2) {
    // MM:SS
    return parts[0] * 60 + parts[1]
  } else if (parts.length === 1) {
    // SS
    return parts[0]
  }

  return 0
}

/**
 * 计算飞行总时间（基于航点和速度）
 * @param {Array} routePositions - 航线航点数据
 * @param {Object} routeSettings - 航线设置
 * @returns {Object} 包含总时间、航段时间等信息
 */
export function calculateFlightTime (routePositions, routeSettings) {
  if (!routePositions || routePositions.length < 2) {
    return {
      totalTime: 0,
      waypointTimes: [],
      totalDistance: 0
    }
  }

  let totalTime = 0
  let totalDistance = 0
  const waypointTimes = []

  // 计算起飞时间（如果有起飞设置）
  if (routeSettings.takeOffSecurityHeight > 0) {
    const takeOffTime = routeSettings.takeOffSecurityHeight / (routeSettings.autoFlightSpeed || 1)
    totalTime += takeOffTime
  }

  // 计算航段时间和距离
  for (let i = 0; i < routePositions.length - 1; i++) {
    const startPos = routePositions[i].position
    const endPos = routePositions[i + 1].position

    // 计算距离（简化版，实际应使用球面距离计算）
    const distance = calculateDistance3D(startPos, endPos)
    totalDistance += distance

    // 使用航点速度或全局速度
    const speed = routePositions[i].waypointSpeed || routeSettings.autoFlightSpeed

    // 计算时间
    const time = distance / Math.max(speed, 0.1) // 防止除以0
    totalTime += time

    waypointTimes.push({
      waypointIndex: i + 1,
      cumulativeTime: totalTime,
      segmentTime: time,
      distance,
      speed
    })
  }

  return {
    totalTime,
    waypointTimes,
    totalDistance,
    formattedTotalTime: formatTime(totalTime)
  }
}

/**
 * 计算3D距离（欧几里得距离）
 */
function calculateDistance3D (point1, point2) {
  if (!point1 || !point2) return 0

  const dx = point2.x - point1.x
  const dy = point2.y - point1.y
  const dz = point2.z - point1.z

  return Math.sqrt(dx * dx + dy * dy + dz * dz)
}

/**
 * 生成时间轴数据（用于进度条标记）
 * @param {Array} routePositions - 航线航点数据
 * @param {Object} routeSettings - 航线设置
 * @returns {Array} 时间轴标记数据
 */
export function generateTimeAxis (routePositions, routeSettings) {
  const result = []
  const flightTime = calculateFlightTime(routePositions, routeSettings)

  if (flightTime.totalTime === 0) return result

  let accumulatedTime = 0

  // 起飞标记（如果有）
  if (routeSettings.takeOffSecurityHeight > 0) {
    const takeOffTime = routeSettings.takeOffSecurityHeight / (routeSettings.autoFlightSpeed || 1)
    result.push({
      type: 'takeoff',
      time: 0,
      label: '起飞',
      positionPercent: 0
    })
    accumulatedTime += takeOffTime
  }

  // 航点标记
  for (let i = 0; i < routePositions.length; i++) {
    const waypoint = routePositions[i]
    const progress = accumulatedTime / flightTime.totalTime

    result.push({
      type: 'waypoint',
      index: i + 1,
      time: accumulatedTime,
      label: `航点${i + 1}`,
      positionPercent: progress,
      actions: waypoint.eventList || []
    })

    // 如果是最后一个航点，不添加航段时间
    if (i < routePositions.length - 1) {
      const startPos = waypoint.position
      const endPos = routePositions[i + 1].position
      const distance = calculateDistance3D(startPos, endPos)
      const speed = waypoint.waypointSpeed || routeSettings.autoFlightSpeed
      const segmentTime = distance / Math.max(speed, 0.1)

      accumulatedTime += segmentTime
    }
  }

  // 结束标记
  result.push({
    type: 'finish',
    time: flightTime.totalTime,
    label: '结束',
    positionPercent: 1
  })

  return result
}

/**
 * 根据进度计算时间
 * @param {number} progress - 进度 (0-1)
 * @param {number} totalTime - 总时间（秒）
 * @returns {Object} 包含当前时间、格式化时间等
 */
export function getTimeByProgress (progress, totalTime) {
  const currentTime = totalTime * Math.max(0, Math.min(1, progress))

  return {
    currentTime,
    formattedCurrentTime: formatTime(currentTime),
    formattedRemainingTime: formatTime(totalTime - currentTime),
    progressPercent: (currentTime / totalTime * 100).toFixed(1)
  }
}

/**
 * 格式化持续时间（用于显示耗时）
 * @param {number} startTime - 开始时间戳
 * @param {number} endTime - 结束时间戳
 * @returns {string} 格式化的持续时间
 */
export function formatDuration (startTime, endTime) {
  const duration = Math.max(0, endTime - startTime) / 1000 // 转换为秒
  return formatTime(duration)
}
